import type { Moment } from '@eformulieren/model'
import { useCallback, useEffect, useRef, useState } from 'react'
import {
  SAVED_MS,
  WRITE_THROUGH_MS,
  type StorageState,
  IDLE_MS,
  DELAY_MS,
} from './storage.js'

/**
 * Het opslaan van de invoer, met de drie dingen die R-163 en R-165 vragen.
 *
 * 1. **Gedebounced wegschrijven** zodra het typen even stilstaat.
 * 2. **Minstens elke twee minuten doorschrijven** zolang er iets openstaat (R-163).
 * 3. **Bij het verlaten of wisselen van een scherm** sturen wat nog openstaat, en
 *    waarschuwen voordat niet-opgeslagen invoer verloren gaat (R-163, R-165).
 *
 * In M2 is `bewaar` een callback van de aanroeper en gebeurt er lokaal niets meer dan
 * dat; vanaf M3 is het de schrijfactie naar de pod. De hook weet daar niets van, en
 * dat is met opzet: dan verandert er in M3 niets aan dit gedrag.
 */
export interface Storage {
  readonly stand: StorageState
  /** Er is iets gewijzigd; de hook bepaalt wanneer er wordt weggeschreven. */
  readonly reportChange: () => void
  /** Nu wegschrijven, ongeacht de rust. Voor indienen en voor schermwissel. */
  readonly writeNow: () => Promise<void>
  /** Of er nog iets openstaat; de verliesbescherming hangt hieraan (R-165). */
  readonly openstaand: boolean
  /**
   * Het moment van de laatste geslaagde opslag, of niets zolang die nog niet is
   * gebeurd (f2, R-157, geen eigen eisnummer).
   *
   * Uit `nu()` en niet uit `Date.now()`: dezelfde injecteerbare klok als de rest van
   * `Invulformulier` (`nu: () => Moment`), zodat een test hem vast kan zetten.
   */
  readonly laatstOpgeslagenOp: Moment | null
}

export function useStorage(save: () => Promise<void>, now: () => Moment): Storage {
  const [state, setState] = useState<StorageState>('rustig')
  const [outstanding, setOutstanding] = useState(false)
  const [lastSavedAt, setLastSavedAt] = useState<Moment | null>(null)

  const saveRef = useRef(save)
  saveRef.current = save
  const nowRef = useRef(now)
  nowRef.current = now

  const idleTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const delay = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const savedTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const busy = useRef(false)

  const writeNow = useCallback(async (): Promise<void> => {
    if (busy.current) return
    busy.current = true
    clearTimeout(idleTimer.current)
    // De indicator verschijnt pas als het lang genoeg duurt om te zien; anders
    // flikkert hij bij elke aanslag (valkuil 7).
    delay.current = setTimeout(() => setState('bezig'), DELAY_MS)
    try {
      await saveRef.current()
      clearTimeout(delay.current)
      setOutstanding(false)
      setState('bewaard')
      setLastSavedAt(nowRef.current())
      clearTimeout(savedTimer.current)
      savedTimer.current = setTimeout(() => setState('rustig'), SAVED_MS)
    } catch {
      // Een mislukte opslag meldt zich direct en blijft staan (R-80, R-141). De
      // oorzaakketen hoort in het log van de pod; de invuller krijgt een melding die
      // hij begrijpt (repo-afspraak over foutketens).
      clearTimeout(delay.current)
      // De terugval naar rust van een eerdere geslaagde opslag mag deze melding niet wegzetten.
      clearTimeout(savedTimer.current)
      setState('mislukt')
    } finally {
      busy.current = false
    }
  }, [])

  const reportChange = useCallback(() => {
    setOutstanding(true)
    clearTimeout(idleTimer.current)
    idleTimer.current = setTimeout(() => void writeNow(), IDLE_MS)
  }, [writeNow])

  // Doorschrijven op cadans, ook als iemand blijft typen (R-163).
  useEffect(() => {
    const clock = setInterval(() => {
      if (outstanding) void writeNow()
    }, WRITE_THROUGH_MS)
    return () => clearInterval(clock)
  }, [outstanding, writeNow])

  // Bij het verlaten of wisselen van een scherm sturen wat nog openstaat (R-163),
  // en waarschuwen zolang er iets openstaat (R-165). `pagehide` en niet `unload`:
  // die laatste breekt de terugkeer uit de cache van de browser.
  useEffect(() => {
    const onLeave = (event: BeforeUnloadEvent): void => {
      if (!outstanding) return
      event.preventDefault()
      // De tekst komt van de browser; die van ons wordt genegeerd. Wat wij kunnen
      // doen is de waarschuwing aanzetten, en dat is wat R-165 vraagt.
      event.returnValue = ''
    }
    const onSwitch = (): void => {
      if (document.visibilityState === 'hidden' && outstanding) void writeNow()
    }
    window.addEventListener('beforeunload', onLeave)
    window.addEventListener('pagehide', onSwitch)
    document.addEventListener('visibilitychange', onSwitch)
    return () => {
      window.removeEventListener('beforeunload', onLeave)
      window.removeEventListener('pagehide', onSwitch)
      document.removeEventListener('visibilitychange', onSwitch)
    }
  }, [outstanding, writeNow])

  useEffect(
    () => () => {
      clearTimeout(idleTimer.current)
      clearTimeout(delay.current)
      clearTimeout(savedTimer.current)
    },
    [],
  )

  return { stand: state, reportChange, writeNow, openstaand: outstanding, laatstOpgeslagenOp: lastSavedAt }
}
